/** @typedef {{ id: string; label: string; note: string; code: string }} GateCheck */
/** @typedef {{ id: string; day: string; task: string; tag: string; key?: boolean; good?: string; bad?: string }} SprintTask */
/** @typedef {{ id: string; tier: number; num: string; title: string; where: string; why: string; firstVersion: string; signal: string; phase?: string; script?: string }} Play */

export const STORAGE_KEY = "rc_activation_v3";
export const DTN_SKIP_KEY = "rc_dtn_skipped";

export const PRESET_CITY_CHIPS = [
  { key: "nyc", label: "New York", upcoming: false },
  { key: "miami", label: "Miami", upcoming: false },
  { key: "chicago", label: "Chicago ↗", upcoming: true },
  { key: "la", label: "Los Angeles ↗", upcoming: true },
];

/** @type {GateCheck[]} */
export const GATE_CHECKS = [
  {
    id: "g1",
    label: "/nyc route exists in AppWrapper.js",
    note: "Verify: navigate to runcards.app/nyc — should redirect to /run with NYC runs immediately.",
    code: '<Route path="/nyc" element={<Navigate to="/run?city=New+York" replace />} />',
  },
  {
    id: "g2",
    label: "City filter is server-side (not just client-side string match)",
    note: "Verify: RunPage.js passes city to getAll() API call. meetings.py has ?city= filter.",
    code: "apiService.meetings.getAll({ limit: 50, city: searchCity })",
  },
  {
    id: "g3",
    label: "Auth loading gates removed — page renders immediately",
    note: 'Verify: open /nyc in incognito. No blank "Loading..." screen. Runs appear within 1s.',
    code: "Remove: if (loading) return <Loading/> from AppWrapper.js + RunPage.js",
  },
  {
    id: "g4",
    label: "/nyc/this-week exists as a flat day-grouped list",
    note: "Verify: navigate to runcards.app/nyc/this-week — NYC runs grouped by day, no carousel, no filters.",
    code: "New component: ThisWeekPage.js + route /nyc/this-week",
  },
  {
    id: "g5",
    label: "OG preview confirmed in iMessage before placing QR cards",
    note: "Test: send runcards.app/nyc in iMessage. Should show title + description + image card.",
    code: "public/index.html: og:title, og:description, og:image",
  },
  {
    id: "g6",
    label: "RunPage hero hidden when city arrives from URL params",
    note: "Verify: open /nyc — straight to runs grid, no full-screen hero or search bar overlay.",
    code: "RunPage.js: skip hero section when searchCity populated from URL",
  },
];

/** @type {{ week1: SprintTask[]; week2: SprintTask[] }} */
export const SPRINT_TASKS = {
  week1: [
    {
      id: "w1d1a",
      day: "Mon",
      task: "Send 10 club DMs — data verification framing (Step 1 script)",
      tag: "outreach",
      key: true,
      good: 'They reply "yes it looks right" or "can you fix X" — relationship started.',
      bad: "You pitch the product in message 1. That closes the door.",
    },
    {
      id: "w1d1b",
      day: "Mon",
      task: "Identify 3 high-priority anchor clubs: dense, consistent, social",
      tag: "outreach",
      good: "You have 3 named clubs with a captain contact for each.",
      bad: "A list of 10 clubs with no one specific to contact.",
    },
    {
      id: "w1d2",
      day: "Tue",
      task: "Follow up with any quick responders — move to Step 2 script",
      tag: "outreach",
      good: "They understand RunCards solves their DM problem.",
      bad: "You explain the product or send a feature list.",
    },
    {
      id: "w1d3a",
      day: "Wed",
      task: "Build /nyc + /nyc/this-week — mobile URL, day-grouped list, no login, loads in <3s",
      tag: "digital",
      key: true,
      good:
        "Open runcards.app/nyc in incognito on phone. NYC runs visible in under 3 seconds without touching anything.",
      bad: 'Page shows "Loading..." for >1 second, or shows a hero image before any runs, or lands on the homepage.',
    },
    {
      id: "w1d3b",
      day: "Wed",
      task: "Add OG meta tags to index.html so /nyc link previews show title + image in iMessage/WhatsApp",
      tag: "digital",
      good: 'Send runcards.app/nyc in iMessage. Preview card shows "NYC Runs This Week" title + image. Takes 30 min.',
      bad: "Backend already has share.py for image generation — don't rebuild that. The gap is static og: tags in public/index.html.",
    },
    {
      id: "w1d4",
      day: "Thu",
      task: "Attend or coordinate with 1–2 runs — goal: 1 organizer says the line",
      tag: "physical",
      key: true,
      good:
        'Organizer says "If you want to find other runs this week, check RunCards" before the run.',
      bad: "You explain the app to someone. You hand out cards without a mention happening.",
    },
    {
      id: "w1d5",
      day: "Thu",
      task: 'Watch for "what\'s that?" phone-sharing moments — log any sightings',
      tag: "physical",
      good: "Someone leans over and looks at another person's phone showing RunCards.",
      bad: "You showed it to them. They didn't ask.",
    },
    {
      id: "w1d6a",
      day: "Sat",
      task: "Place 10–20 QR cards: McCarren, Central Park, Prospect Park",
      tag: "physical",
      good: "Organizer places the card, not you. Implicit endorsement.",
      bad: "You placed them yourself with no organizer connection.",
    },
    {
      id: "w1d6b",
      day: "Sat",
      task: 'Post "Running in NYC this week?" once in r/RunNYC (genuine resource tone)',
      tag: "digital",
      good: 'Response: "thanks, bookmarked this".',
      bad: 'Response: "cool app!" — they saw it as a promo, not a resource.',
    },
  ],
  week2: [
    {
      id: "w2d1a",
      day: "Mon",
      task: "Follow up with clubs that replied — lock in 2–3 for mentions",
      tag: "outreach",
      good: 'Club captain says "yeah I\'ll mention it Thursday".',
      bad: 'They say "sounds cool" — that\'s not a commitment.',
    },
    {
      id: "w2d1b",
      day: "Mon",
      task: "Ask 1 club to add RunCards link to Strava bio or newsletter",
      tag: "outreach",
      good: "They add it same day without asking why.",
      bad: 'They ask "what do we get out of it?" — reframe as utility for their runners.',
    },
    {
      id: "w2d2a",
      day: "Tue",
      task: "Seed 5 runners (not leaders) to screenshot RunCards in chats",
      tag: "outreach",
      key: true,
      good: "5 specific people with names, each in a different club chat.",
      bad: 'You told "a few people" — vague seeding doesn\'t compound.',
    },
    {
      id: "w2d2b",
      day: "Tue",
      task: "Give them the script: when someone asks what's tomorrow → send the screenshot",
      tag: "outreach",
      good: "They understood in one sentence. No further explanation needed.",
      bad: "You explained the app to them. Now they'll explain it instead of just sending the screenshot.",
    },
    {
      id: "w2d4a",
      day: "Thu",
      task: 'Same runs as Week 1 — watch for organic "what\'s that?" moments',
      tag: "physical",
      key: true,
      good: "Someone who wasn't seeded asks about RunCards because they saw it on someone else's phone.",
      bad: "All opens are from your seeds — the behavior hasn't spread yet.",
    },
    {
      id: "w2d4b",
      day: "Thu",
      task: "Check for post-run traffic spikes (Today + Tomorrow views = planning signal)",
      tag: "digital",
      good: "Traffic spike between 7:00–8:30 AM Thursday matches your run start time.",
      bad: "No spike — the mention isn't happening or not triggering check behavior yet.",
    },
    {
      id: "w2d5",
      day: "Fri",
      task: "Log all real signals: mentions, screenshots shared, new faces at runs",
      tag: "outreach",
      good: "3–5 distinct signals logged from different people.",
      bad: "1 signal from the same person who always checks it.",
    },
    {
      id: "w2d7a",
      day: "Sun",
      task: "Review: Did anyone check RunCards before a run this week?",
      tag: "digital",
      key: true,
      good: "Yes — and you can name the person, the run, and the time.",
      bad: "You think so but can't point to specific evidence.",
    },
    {
      id: "w2d7b",
      day: "Sun",
      task: "Brief on Week 2 learnings — prep expansion template for next city",
      tag: "digital",
      good: "You have 1–2 anchor clubs where signals are self-generating.",
      bad: "You have activity across many clubs but no depth anywhere.",
    },
  ],
};

export const KEY_TASKS_FLAT = [
  ...SPRINT_TASKS.week1.filter((t) => t.key),
  ...SPRINT_TASKS.week2.filter((t) => t.key),
];

/** @type {Play[]} */
export const PLAYS = [
  {
    id: "p1",
    tier: 1,
    num: "01",
    title: "The Club Captain Handshake",
    where: "Direct outreach — NYC run club organizers",
    why: "Everything else is downstream of this. Organizer relationships unlock pre-run mentions, WhatsApp seeding, Strava bios, post-run prompts.",
    firstVersion:
      'Email 10 clubs. Not a pitch — "Is your data accurate on RunCards?" Then once rapport exists: "Feel free to mention it if useful." No ask in message 1.',
    signal: "2 organizers say the sentence before a run. That's it. That's the flywheel starting.",
    phase: "Do this first. Everything else follows.",
    script:
      "Hey — quick question.\n\nWe mapped your weekly runs into RunCards (NYC run schedule). Just wanted to check — is everything accurate on your end?\n\nHappy to fix anything.",
  },
  {
    id: "p2",
    tier: 1,
    num: "02",
    title: "The Pre-Run Mention",
    where: "Physical — at the start of runs, during organizer briefing",
    why: "Leaders already hold attention for 60 seconds before every run. Borrowing that moment is borrowing context authority.",
    firstVersion:
      'Give organizers a one-sentence script: "If you\'re ever wondering what else is happening this week, RunCards has everything mapped out." Nothing more.',
    signal: "Direct traffic spikes within 15 minutes of a run start time.",
    phase: "Do this once you have 2–3 organizers warm.",
  },
  {
    id: "p3",
    tier: 1,
    num: "03",
    title: "The Post-Run Moment",
    where: "Physical — cool-down circle, post-run standing groups",
    why: '"What\'s next?" is already asked after every run. You\'re not creating behavior. You\'re inserting the tool into a question that already exists.',
    firstVersion:
      'Organizer says one sentence at cool-down: "If you want to find more like this, RunCards has the full week."',
    signal: "Post-run traffic on Tue/Thu/Sat evenings correlating with your partner clubs.",
    phase: "Works once organizer relationship is established.",
  },
  {
    id: "p4",
    tier: 2,
    num: "04",
    title: "The Traveling Runner Hook",
    where: 'Digital — "Running in NYC this week?" page',
    why: "Travelers have maximum uncertainty + zero allegiance = highest conversion.",
    firstVersion: "One mobile-optimized URL. Time-based: Today → Tomorrow → Week. No login. No friction.",
    signal: "Users viewing from non-NYC IPs who return from NYC.",
    phase: "Build the page. Low effort, steady stream of high-intent users.",
  },
  {
    id: "p5",
    tier: 2,
    num: "05",
    title: "The Chat Interception",
    where: "Digital — WhatsApp/iMessage group chats of run clubs",
    why: 'The question "what\'s happening tomorrow?" already exists in these chats constantly.',
    firstVersion:
      'Identify 5 runners across 3 clubs. Give them one instruction: "When someone asks what\'s tomorrow — send a RunCards screenshot. No explanation."',
    signal: "Screenshots get reused by others (not just your seeds).",
    phase: "Week 2 of the sprint.",
  },
  {
    id: "p6",
    tier: 2,
    num: "06",
    title: "The QR Drop",
    where: "Physical — McCarren Park, Central Park loops, Prospect Park",
    why: "Hits runners in the 5-minute idle window before runs when uncertainty is highest.",
    firstVersion:
      'Minimal weatherproof card: "Find runs this week" + QR → /nyc. Placed by organizer — that matters.',
    signal: "QR scans clustered in 5:30–7:00 AM and 6:00–8:00 PM windows.",
    phase: "Runs parallel to organizer outreach.",
  },
  {
    id: "p7",
    tier: 3,
    num: "07",
    title: 'The "This Week" Weekly Artifact',
    where: "Digital — IG Story, shared via clubs, texted in chats",
    why: "Sunday night is when runners mentally map their week.",
    firstVersion: 'One clean story: "This week in NYC." Minimal list. Key: clubs post it, not RunCards.',
    signal: "Story saves > likes. Users returning Monday morning.",
    phase: "Once 2+ clubs are active partners.",
  },
  {
    id: "p8",
    tier: 3,
    num: "08",
    title: "The Accountability Share",
    where: "Digital — iMessage link previews",
    why: "A shareable run link that renders well in iMessage enables the coordination behavior that already exists.",
    firstVersion: "Ensure every run detail page has a clean share URL. Fix OG meta tags in public/index.html.",
    signal: "Direct traffic arriving via link previews (no referrer, mobile device, specific run page).",
    phase: "Quick win — fix the link preview metadata.",
  },
  {
    id: "p9",
    tier: 3,
    num: "09",
    title: "The Strava Bio Layer",
    where: "Digital — Strava club pages",
    why: "Many clubs already have active Strava pages. Members checking weekly are a high-intent audience.",
    firstVersion:
      'Ask partner clubs to add: "Find our runs and others on RunCards: [link]" to their Strava club description.',
    signal: "Referral traffic from Strava that converts to repeat visits.",
    phase: "Ask during Week 2 follow-ups.",
  },
  {
    id: "p10",
    tier: 3,
    num: "10",
    title: "The Week View as Habit Anchor",
    where: "Product — routing logic",
    why: "Weekly planning behavior already exists. Runners mentally plan on Sunday/Monday.",
    firstVersion: "Route Monday/Tuesday arrivals to the Week view by default.",
    signal: "Week view sessions that result in 2+ run detail page views.",
    phase: "Small product change.",
  },
];

export const CLUBS_DEFAULT = [
  {
    id: "c1",
    name: "North Brooklyn Runners",
    loc: "Brooklyn / McCarren Park",
    runs: "Tue AM, Thu AM, Sat AM",
    priority: "high",
    status: "pending",
    notes: "Very active IG, 800+ members, great community energy",
  },
  {
    id: "c2",
    name: "NYRR Run Group",
    loc: "Manhattan / Central Park",
    runs: "Mon AM, Wed AM, Sat AM",
    priority: "high",
    status: "pending",
    notes: "Large established community, formal structure",
  },
  {
    id: "c3",
    name: "Prospect Park Track Club",
    loc: "Brooklyn / Prospect Park",
    runs: "Tue AM, Thu PM, Sun AM",
    priority: "high",
    status: "pending",
    notes: "Strong recurring attendance, consistent times",
  },
  {
    id: "c4",
    name: "West Side Runners",
    loc: "Manhattan / Hudson River",
    runs: "Tue PM, Thu PM, Sat AM",
    priority: "mid",
    status: "pending",
    notes: "PM slots, good for coverage",
  },
  {
    id: "c5",
    name: "Old Man Run Club",
    loc: "Manhattan / Various",
    runs: "Wed AM, Fri AM",
    priority: "mid",
    status: "pending",
    notes: "Well known in community, social-first vibe",
  },
  {
    id: "c6",
    name: "Orchard Street Runners",
    loc: "LES / Manhattan",
    runs: "Tue PM, Thu PM",
    priority: "mid",
    status: "pending",
    notes: "Young crowd, very active WhatsApp group",
  },
  {
    id: "c7",
    name: "Black Roses NYC",
    loc: "Brooklyn / Various",
    runs: "Mon PM, Wed PM",
    priority: "mid",
    status: "pending",
    notes: "Culture-forward, strong community",
  },
  {
    id: "c8",
    name: "Mile High Run Club",
    loc: "Midtown / Manhattan",
    runs: "Mon AM, Wed PM, Fri AM",
    priority: "low",
    status: "pending",
    notes: "Studio-based",
  },
  {
    id: "c9",
    name: "Track Mafia",
    loc: "Queens / Various",
    runs: "Tue AM, Thu AM",
    priority: "mid",
    status: "pending",
    notes: "Speed-focused, Queens base",
  },
  {
    id: "c10",
    name: "Dashing Whippets",
    loc: "Brooklyn / Various",
    runs: "Wed AM, Sat AM",
    priority: "low",
    status: "pending",
    notes: "Established club, competitive-leaning",
  },
];

export const DM_SCRIPTS = [
  {
    step: "Step 1",
    title: "First Contact — Data Verification",
    context: "Send to all 10 clubs. No ask. No pitch. Signals respect and accuracy.",
    message:
      "Hey — quick question.\n\nWe mapped your weekly runs into RunCards (NYC run schedule). Just wanted to check — is everything accurate on your end?\n\nHappy to fix anything if the time or location is off.",
    note: "No ask. No product pitch. No link. Just: is this right? Wait for a response before Step 2.",
  },
  {
    step: "Step 2",
    title: "After They Respond — Gentle Utility Frame",
    context: "Only send after they've responded to Step 1.",
    message:
      'Perfect — appreciate it.\n\nWe\'re just trying to keep the city schedule clean so runners can find things without digging through posts.\n\nIf you ever want to point people somewhere instead of answering "when/where" questions, this helps: [link]',
    note: 'You\'re giving them a tool, not a product to promote. "Instead of answering DMs" is the key frame.',
  },
  {
    step: "Step 3",
    title: "The Mention Seed (Warm Clubs Only)",
    context: "Only for clubs that have been helpful and seem genuinely interested.",
    message:
      "Also — if it's useful, some clubs just mention it quickly before runs:\n\n\"If you're looking for other runs this week, check RunCards.\"\n\nTotally optional — just figured I'd share what others are doing.",
    note: '"What others are doing" is social proof without pressure. "Totally optional" increases adoption.',
  },
  {
    step: "Step 4",
    title: "Custom Link Offer (High-Trust Only)",
    context: "Only for your 3 anchor clubs.",
    message:
      "We can also give you a clean link just for your club if you want — so runners can tap directly into your schedule on RunCards.\n\nKeeps everything consistent and makes it easy to paste in your bio or before runs.",
    note: "The product serving the organizer, not the other way around.",
  },
  {
    step: "Chat",
    title: "Runner Script — Chat Interception",
    context:
      "Give this to 5 non-leader runners in different clubs. Their only job: send the screenshot.",
    message:
      '[When someone in your run club chat asks "what\'s happening tomorrow?" or "anyone running this week?"]\n\n→ Don\'t explain anything. Just open RunCards and screenshot the Tomorrow or Week view. Send that.\n\nIf they ask what it is: "just a clean list of runs in the city."',
    note: "No explanation needed. The screenshot does the work.",
  },
];

export const FIELD_TASKS = {
  before: [
    {
      id: "fb1",
      text: "Check RunCards Week view — know what runs are happening today",
      note: "You should know this before anyone asks you.",
    },
    {
      id: "fb2",
      text: "Have the app open on your phone — ready to show, not explain",
      note: "Don't open it for the first time in front of someone.",
    },
    {
      id: "fb3",
      text: "Confirm with organizer that they'll do the pre-run mention",
      note: 'One text is enough: "hey you good to mention RunCards today?"',
    },
  ],
  during: [
    {
      id: "fd1",
      text: "Show 1 person RunCards — don't explain, just open it",
      note: 'The goal is a "what\'s that?" reaction, not a demo.',
    },
    {
      id: "fd2",
      text: 'Listen for "what else is there this week?" — that\'s your moment',
      note: "Don't jump in too early. Let the question happen naturally.",
    },
    {
      id: "fd3",
      text: "Watch for phone sharing — note it if it happens",
      note: "This is a strong signal. Log it after.",
    },
  ],
  after: [
    {
      id: "fa1",
      text: "Log any signal that happened — even weak ones count",
      note: "Open Signal Log now, before you forget.",
    },
    {
      id: "fa2",
      text: "Did someone check RunCards before deciding on their next run?",
      note: "If yes — this is a strong signal. Log it with who + when.",
    },
    {
      id: "fa3",
      text: 'Text your chat interception seed: "hey did anyone ask about tomorrow?"',
      note: "Check if your seed deployed the screenshot.",
    },
  ],
};

export const DENSITY_SIGNALS = [
  { id: "organizer_mention", label: "Organizer mentioned it at a run" },
  { id: "phone_share", label: "Phone-sharing moment observed" },
  { id: "chat_intercept", label: "Chat interception deployed" },
  { id: "qr_placed", label: "QR card placed by organizer" },
  { id: "unprompted_check", label: "Someone checked unprompted" },
];

export const SOURCE_LABELS = {
  qr: "QR",
  organizer: "Organizer",
  chat: "Chat",
  traveler: "Traveler",
  other: "Other",
};

export const KILL_LIST_ITEMS = [
  "Instagram growth",
  "Paid ads",
  "Influencers",
  "SEO expansion",
  "New cities (until NYC is self-sustaining)",
  "More than 3 anchor clubs at once",
  "Explaining the app",
];

/** @param {string} id */
export function getTaskScript(id) {
  const map = {
    w1d1a: DM_SCRIPTS[0].message,
    w1d4:
      '"If you\'re looking for other runs this week, check RunCards." — Say this or give it to the organizer to say before the run. QR destination: runcards.app/nyc',
    w1d3a: `BUILD CHECKLIST:
1. AppWrapper.js → add route: <Route path="/nyc" element={<Navigate to="/run?city=New+York" replace />} />
2. RunPage.js → remove auth loading gate (if loading return <Loading/>)
3. New file: ThisWeekPage.js → flat day-grouped list, city=New+York, no carousel
4. AppWrapper.js → add route: <Route path="/nyc/this-week" element={<ThisWeekPage />} />

Test: open runcards.app/nyc in incognito. NYC runs visible in <3 seconds.`,
    w1d3b: `FIX: public/index.html
Add:
<meta property="og:title" content="NYC Runs This Week — RunCards"/>
<meta property="og:description" content="Find group runs in New York City this week."/>
<meta property="og:image" content="https://runcards.app/og-nyc.jpg"/>
<meta name="twitter:card" content="summary_large_image"/>

Also fix RunPage.js: pass city to API
→ apiService.meetings.getAll({ limit: 50, city: searchCity })
And meetings.py: add ?city= MongoDB filter.`,
    w2d2a: DM_SCRIPTS[4].message,
    w2d4a: `Watch — don't act. You're looking for someone who wasn't seeded asking about RunCards because they saw it on someone else's phone. Log it if it happens.`,
    w2d7a: `Look at Signal Log. Can you name 1 specific person + 1 specific run + 1 specific time when they checked RunCards before deciding to go?`,
  };
  return map[id] || null;
}

export const KPI_DETAIL_COPY = {
  mentions: {
    label: "Pre-run Mentions",
    desc: "The number of organizers who have said the one-sentence mention before a run. Primary leading indicator for habit formation.",
  },
  clubs: {
    label: "Clubs Activated",
    desc: 'Clubs at "active" or "anchor" status. Target 3 deep before expanding.',
  },
  sharing: {
    label: "Chat Intercepts",
    desc: "Runners seeded with the screenshot behavior who have deployed it. Goal: screenshots get reused by non-seeds.",
  },
  travelers: {
    label: "Traveler Page Views",
    desc: "Weekly views from non-local IPs. Travelers convert at high rates — low maintenance, steady acquisition.",
  },
};

export const TAB_IDS = ["sprint", "playbook", "clubs", "scripts", "copypaste"];
